import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Drawer, IconButton } from '@material-tailwind/react';
import { X } from 'lucide-react';
import { Sidebar, TopNav, Breadcrumbs } from '@/components/navigation';
import { GlobalSearch } from '@/components/ui';
import { useGlobalSearchShortcut } from '@/hooks';
import { useUIStore } from '@/store/uiStore';

/**
 * Main dashboard layout component
 * Features a responsive sidebar, top navigation, and main content area
 * Sidebar collapses to drawer on mobile devices
 */
export function DashboardLayout(): React.ReactElement {
  const { sidebarOpen, setSidebarOpen, toggleSidebar, openSearch } = useUIStore();

  // Register global search shortcut (Cmd/Ctrl+K)
  useGlobalSearchShortcut(openSearch);

  /**
   * Handle responsive sidebar behavior
   * Auto-collapse on mobile, auto-expand on desktop
   */
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      if (isDesktop && !sidebarOpen) {
        setSidebarOpen(true);
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setSidebarOpen, sidebarOpen]);

  /**
   * Close mobile drawer when clicking overlay
   */
  const closeMobileDrawer = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-phish-bg-0">
      {/* Desktop Sidebar - Fixed on left */}
      <aside
        className={`fixed left-0 top-0 z-20 h-full w-[248px] transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } hidden lg:block`}
      >
        <Sidebar className="h-screen w-full" />
      </aside>

      {/* Mobile Drawer */}
      <Drawer
        open={sidebarOpen && window.innerWidth < 1024}
        onClose={closeMobileDrawer}
        className="lg:hidden border-r border-phish-border bg-phish-bg-1 shadow-none"
        overlay={true}
        size={248}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-end p-2">
            <IconButton
              variant="text"
              color="gray"
              onClick={toggleSidebar}
              aria-label="Close sidebar"
              className="text-phish-fg-2 hover:bg-white/5"
            >
              <X className="h-6 w-6" strokeWidth={1.5} />
            </IconButton>
          </div>
          <Sidebar className="flex-1" />
        </div>
      </Drawer>

      {/* Main Content Area */}
      <div
        className={`transition-all duration-300 ${
          sidebarOpen ? 'lg:ml-[248px]' : 'lg:ml-0'
        }`}
      >
        {/* Top Navigation */}
        <TopNav />

        {/* Page Content */}
        <main className="min-h-[calc(100vh-4rem)]">
          {/* Breadcrumbs */}
          <div className="px-4 py-4 lg:px-8">
            <Breadcrumbs />
          </div>

          {/* Page Outlet */}
          <div className="px-4 pb-8 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile Overlay (when drawer is open) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-10 bg-black/50 lg:hidden"
          onClick={closeMobileDrawer}
          aria-hidden="true"
        />
      )}

      {/* Global Search Modal */}
      <GlobalSearch />
    </div>
  );
}

export default DashboardLayout;
